import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import UltimatePagination from 'react-ultimate-pagination-bootstrap-3';
import { Form } from 'react-bootstrap-5';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';

import { getCustomerData } from './api';

export default function SyndicationBrokerPartnerTable({ groupedOffices, customerPartnerDataPath, isParentCompany }) {
  const [partners, setPartners] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [totalPagesCount, setTotalPagesCount] = useState(1);
  const [params, setParams] = useState({ page: 1, search: '', office: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData(params);
  }, []);

  const fetchData = (requestParams) => {
    setLoading(true);
    getCustomerData(customerPartnerDataPath, requestParams).then((json) => {
      setPartners(json.partners);
      setBrokers(json.brokers);
      setTotalPagesCount(json.totalPagesCount);
      setLoading(false);
    });
  };

  const updateParams = (newParams) => {
    setParams(newParams);
    fetchData(newParams);
  };

  const handleOnPageChange = (newPage) => {
    updateParams({ ...params, page: newPage });
  };

  const handleOnRowClick = (userPath) => {
    window.location.href = userPath;
  };

  const debouncedSearch = debounce((text) => {
    updateParams({ ...params, page: 1, search: text });
  }, 400);

  const handleSearchChange = (event) => {
    debouncedSearch(event.target.value);
  };

  const handleOfficeChange = (event) => {
    updateParams({ ...params, page: 1, office: event.target.value });
  };

  const defaultOfficeOption = () => {
    return (
      <option value="">All Offices</option>
    );
  };

  const buildGroupedOfficeOptions = () => {
    if (isParentCompany) {
      return groupedOffices.map((group) => {
        return (
          <optgroup key={group.companyId} label={group.companyName}>
            { group.offices.map((office) => {
              return (
                <option key={office.id} value={office.id}>{office.name}</option>
              );
            }) }
          </optgroup>
        );
      });
    } else {
      return groupedOffices.map((office) => {
        return (
          <option key={office.id} value={office.id}>{office.name}</option>
        );
      });
    }
  };

  const partnerHeaderData = (partner) => {
    if (partner.favicon.exists) {
      return (
        <img alt={partner.name} src={partner.favicon.url} title={partner.name} />
      );
    } else {
      return partner.name;
    }
  };

  const partnersTableHeader = () => {
    return partners.map((partner) => {
      return (
        <th className="partner-col" key={partner.id} title={partner.name}>
          { partnerHeaderData(partner) }
        </th>
      );
    });
  };

  const renderSinglePartnerBrokerStatusColumn = (user, partner) => {
    const blankStatus = { status: '', icon: '', label: '' };
    const partnerStatus = user.partnersStatus.find(ps => ps.partnerId === partner.id)?.partnerStatus || blankStatus;

    return (
      <td className={`partner-col partner-icon ${partnerStatus.status}`} key={partner.id}
        title={partnerStatus.status == 'not-present' ? 'No Credentials Provided' : ''}
      >
        <i className={`fas ${partnerStatus.icon}`} title={partnerStatus.label}></i>
      </td>
    );
  };

  const renderBrokerPartnersStatus = (user) => {
    return partners.map((partner) => {
      if (partner.onHold) {
        return (
          <td className="partner-col partner-icon unconfirmed" key={partner.id}>
            <i className="fas fa-minus-circle" title="On Hold"></i>
          </td>
        );
      } else {
        return renderSinglePartnerBrokerStatusColumn(user, partner);
      }
    });
  };

  const renderBrokers = () => {
    return brokers.map((user) => {
      return (
        <tr className={`user clickable-row ${user.disabled ? 'disabled' : ''}`} key={user.id}
          onClick={() => handleOnRowClick(user.editPath)}
        >
          <td className="frozen-col profile-photo">
            <img alt={user.name} src={user.profilePhotoUrl} />
          </td>
          <td className="frozen-col name">
            <strong>{user.name}</strong>
            <span className="text-muted">{user.jobTitle}</span>
          </td>
          <td className="frozen-col office hidden-xs">{user.companyOfficeLabel}</td>
          { renderBrokerPartnersStatus(user) }
        </tr>
      );
    });
  };

  const renderTable = () => {
    return (
      <React.Fragment>
        <div className="user-table scrollable">
          <table className="table table-hover">
            <thead>
              <tr>
                <th className="frozen-col profile-photo"></th>
                <th className="frozen-col name">Name</th>
                <th className="frozen-col office">Office</th>
                { partnersTableHeader() }
              </tr>
            </thead>
            <tbody>
              { renderBrokers() }
            </tbody>
          </table>
        </div>

        <div className="pagination-container">
          <UltimatePagination
            currentPage={totalPagesCount > 0 ? params.page : 0}
            totalPages={totalPagesCount}
            onChange={handleOnPageChange}
          />
        </div>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <div className="broker-tab">
        <h3>Brokers Registered for Syndication</h3>
        <form className="filters filters-container">
          <div className="hidden-sm hidden-xs hidden-md filters-item">
            <label>Select Office</label>
            <select className="form-control" onChange={handleOfficeChange}>
              { defaultOfficeOption() }
              { buildGroupedOfficeOptions() }
            </select>
          </div>

          <div className="filters-item filters-search">
            <label className="hidden-xs">Search Users</label>
            <br className="hidden-xs" />
            <Form.Control placeholder="Search Users" type="text" onChange={handleSearchChange}/>
          </div>
        </form>
        <br />

        { loading && (
          <div className="loading text-center">
            <FontAwesomeIcon className="fa-spin" icon={faSpinner} size="xl" />
            <br />
          </div>
        )}

        { brokers.length === 0 ? (
          <h4 className="text-muted pl-5">No brokers found</h4>
        ) : renderTable() }
      </div>
    </React.Fragment>
  );
}

SyndicationBrokerPartnerTable.propTypes = {
  customerPartnerDataPath: PropTypes.string,
  groupedOffices: PropTypes.array,
  isParentCompany: PropTypes.bool,
};
